import { zodResolver } from '@hookform/resolvers/zod'
import { useStore } from 'effector-react'
import { useForm } from 'react-hook-form'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'
import { leadLoginFx } from '~/features/auth/Login/model'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { useTaxiparkQueryParams } from '~/shared/lib/useTaxiparkQueryParams'
import { sanitizePhoneNumber } from '~/shared/lib/utils'
import { useStorageRepeatCode } from '~/shared/ui/RepeatCode'
import {
  BasicInformationStep,
  leadRegistrationFormModel,
} from './BasicInformationStep'
import { ConfirmCodeStep } from './ConfirmCodeStep'
import { TIMER_REGISTRATION, TIMER_REGISTRATION_TIME } from './constant'
import { ErrorStep } from './ErrorStep'
import { leadRegistration, leadLogin, leadRegistrationFx } from './model'
import { ReturnToAppStep } from './ReturnToAppStep'
import { $currentStepFlags } from './stepModel'

export const SimplifiedRegistration = () => {
  const { taxiparkIdQuery, xTaxiparkKeyQuery } = useTaxiparkQueryParams()
  const [fromParam] = useQueryParam('from', withDefault(StringParam, ''))
  const { runTimer, getIsTimerWorking } =
    useStorageRepeatCode(TIMER_REGISTRATION)

  const { isFormStep, isConfirmCodeStep, isErrorStep } =
    useStore($currentStepFlags)

  const basicInformationForm = useForm<leadRegistrationFormModel.FormValues>({
    resolver: zodResolver(leadRegistrationFormModel.formSchema),
  })

  const isSubmitting = useStore(leadRegistrationFx.pending)
  const isLoginPending = useStore(leadLoginFx.pending)

  const phone = basicInformationForm.watch('phone')

  const isFromNativeApp = fromParam === 'android' || fromParam === 'ios'

  const handleBasicInformationOnSuccess = (
    formValues: leadRegistrationFormModel.FormValues,
    captchaToken: string,
  ) => {
    if (!taxiparkIdQuery && !xTaxiparkKeyQuery) {
      snackbarEnqueued({
        message: 'Ошибка получения ID таксопарка, обратитесь к администратору',
        variant: 'warning',
      })
      return
    }

    leadRegistration({
      ...formValues,
      taxiparkId: taxiparkIdQuery,
      captchaToken,
    })
  }

  const handleCodeRepeat = () => {
    if (getIsTimerWorking()) {
      snackbarEnqueued({
        message:
          'Запросите код повторно после того как пройдет блокировка получения кода',
        variant: 'warning',
      })
    } else {
      runTimer(TIMER_REGISTRATION_TIME)
    }
  }

  const handleCodeSubmit = (code: string) => {
    if (!phone) {
      snackbarEnqueued({
        message: 'Номер телефона не может быть пустым',
        variant: 'warning',
      })
      return
    }
    leadLogin({ phone: sanitizePhoneNumber(phone), code })
  }

  return (
    <>
      {isFormStep && (
        <BasicInformationStep
          onSuccess={handleBasicInformationOnSuccess}
          form={basicInformationForm}
          isSubmitting={isSubmitting}
        />
      )}

      {isConfirmCodeStep && isFromNativeApp && (
        <ReturnToAppStep
          phone={phone}
          platform={fromParam as 'android' | 'ios'}
        />
      )}

      {isConfirmCodeStep && !isFromNativeApp && (
        <ConfirmCodeStep
          phone={phone}
          onOk={handleCodeSubmit}
          isSubmitting={isLoginPending}
          onCodeRepeat={handleCodeRepeat}
        />
      )}

      {isErrorStep && <ErrorStep />}
    </>
  )
}
